/**
 * NameAndLineageModule
 * ═══════════════════════════════════════════════════════════════
 * Standalone Special-Edition module: "The Name and the Lineage."
 *
 * Four units covering:
 *   1. The Name (Aahaya and the Four Letters)
 *   2. The Truth of the Lineage (Dispelling Misconceptions)
 *   3. The Corporate Son (The Nation of Yisrael)
 *   4. The Historical Veil (Council of Nicaea)
 *
 * Each unit ships TWO teaching layers:
 *   • Youth — concrete, embodied, identity-affirming
 *   • Adult — analytical, etymological, historical deconstruction
 *
 * Renders with the platform-standard:
 *   • InvisibleTeacher (voice-first narration)
 *   • StandardLessonActions (Back / Restart / Quiz / PDF)
 *
 * Pure-frontend — no backend seed required. The full curriculum lives
 * in `UNITS` below and can be deep-expanded with AI per-section using
 * the same protocol the Ordination module uses.
 */
import { useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import InvisibleTeacher from "./InvisibleTeacher";
import StandardLessonActions from "./StandardLessonActions";
import ScriptureSidePanel, { linkifyScripture } from "./ScriptureSidePanel";
import AnimatedHelper from "./AnimatedHelper";
import { clearLessonProgress } from "../utils/lessonProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = [
  {
    id: "name-and-four-letters",
    number: 1,
    title: "Unit 1 — The Name (Aahaya and the Four Letters)",
    subtitle: "The very breath of the Creator",
    foundation:
      "Begin with the essence of existence itself. Before any title, any role, any creation — there is the I AM.",
    sections: [
      {
        title: "Aahaya — The I AM (אהיה)",
        content: `Both "Ehyeh" and "Aahaya" are English transliterations of the same Hebrew word — אֶהְיֶה — and BOTH begin with the consonant Aleph (א). The difference is not in the Hebrew letters; it is in how the vowels under those letters are read out.

It is important to be honest about the scholarly status of the two spellings:

• "EHYEH" is the spelling found in the oldest surviving Hebrew manuscripts (the Masoretic Text, the Dead Sea Scrolls fragments, the Aleppo Codex, the Leningrad Codex). It is the form mainstream Hebrew scholarship — both Jewish and academic Christian — recognizes as the textually accurate vocalization of Exodus 3:14. When a yeshiva student in Yerushalayim reads Exodus 3:14 today, the word on the page is pronounced "Eh-Yeh."

• "AAHAYA" is a variant pronunciation favored by several restoration-faith and Hebrew-Israelite teaching streams. It is a phonetic re-reading that emphasizes the open-throated breath of the Aleph and the rolling "ah" sound. It is found in later interpretive traditions and in the modern Yisraelite restoration movement. It is NOT a different Hebrew word — it is the same אֶהְיֶה, voiced differently.

Why both are taught in this curriculum:

We use "Aahaya" as a teaching device because the breath-emphasis ("ah-hay-ah") makes the lesson about the Creator's name being a VERB-of-being viscerally clear to students — every inhale and exhale becomes a small echo of the Name. But we honor the scholarly record by teaching, alongside it, that "Ehyeh" is what the manuscripts actually say, and that any student going on to academic Hebrew study should expect to encounter "Ehyeh" in every published text.

Three things to teach about the word — true under either pronunciation:

• It is a VERB, not a noun. The Creator does not have a name like a man has a name. The Creator IS the eternal act of being. Every breath we take is a small echo of His self-existence.

• It precedes the Four-Letter Name (YHWH). YHWH is the relational, covenantal form revealed AFTER Ehyeh / Aahaya. The I-AM is the source; YHWH is the covenant.

• It cannot be possessed, branded, or trademarked. No nation, no church, no tradition owns the I-AM. He is the existence of all that exists.

When we open our mouth and breathe, we are speaking a fragment of the I-AM without realizing it. The inhale "ah" and the exhale "hay" form the basic Hebrew root of His being. This is why the rabbis taught that a baby is born already praising the Creator — every breath is "Ah-Hay-Ah."`,
      },
      {
        title: "The Blueprint — YHWH (יהוה) Broken Down",
        content: `YHWH (Yod-Hay-Waw-Hay, יהוה) is the functional template — the four-letter blueprint by which the Most High interacts with creation. Read it forensically:

• יוד (Yod) — The Hand / The Inception
  A single point. The smallest letter in the Hebrew alphabet. It represents the divine FINGER reaching down, the seed-point from which everything else extends. When a Hebrew scribe finishes a Torah scroll, the FIRST and LAST letter he writes is often a Yod — beginning and end are the same divine inception.

• הא (Hay) — The Breath / The Window
  An open shape: three lines, one of them detached. Hebrew tradition teaches the Hay represents an open window or doorway through which breath passes. It is the lung of the Name. When the Creator BREATHES upon Adam (Genesis 2:7), the breath He uses is a Hay.

• וו (Waw) — The Connector / The Nail
  A vertical line — a peg, a hook, a nail. The Waw is the binding force, the conjunction "and." In ancient Hebrew it physically looks like a tent peg holding two cloths together. The Waw is what JOINS heaven to earth, spirit to flesh, intention to action.

• הא (Hay, repeated) — The Completion / The Manifested Breath
  The second Hay is the breath returned — what was inhaled by Yod is now exhaled into the world. Creation is the Creator's second Hay. We, the nation of Yisrael, are part of that second Hay made audible.

Read together: Inception → Breath → Connection → Manifestation. YHWH is the four-step pattern of every act of creation. A seed (Yod) takes in life (Hay), is joined to soil and water (Waw), and grows into a tree (Hay). The Name is not just a label — it is a working schematic of how reality functions.`,
      },
      {
        title: "Youth Teaching Strategy — Hand Motions",
        content: `For young learners, the Four Letters are taught with the body so the Name lives in muscle memory long before it lives in vocabulary:

• Yod — Point your index finger toward the sky, then to yourself. "The Creator REACHED DOWN to make me."
• Hay (first) — Open both palms toward your face, fingers up. "The Creator BREATHED life into me."
• Waw — Stand tall, arms straight at your sides like a peg. "The Creator JOINED me to His family."
• Hay (second) — Open both palms outward, away from you. "I BREATHE His life back into the world."

Have children do all four motions in sequence while singing "Yod—Hay—Waw—Hay" three times. By the end of the lesson, the Name is in their bones. This is the "Creator's Signature" — they should be able to draw it in the air without thinking.

A child who has done this exercise a hundred times will, decades later, recognize the same pattern in a Torah scroll and feel a flash of belonging. That is the goal.`,
      },
      {
        title: "Adult Teaching Strategy — Forensic Etymology",
        content: `For adults, marry the Four-Letter Blueprint to the Forensic Etymology track. Show how Hebrew is a MECHANICAL language — every two- and three-letter root has a physical or functional meaning, and YHWH is not an exception but the supreme example:

• Look at the parent root "HV" (הו) — to be, to exist. It sits inside YHWH.
• Look at the parent root "YH" (יה) — the short form, used in halleluYAH. Yod + first Hay = "the seed of breath."
• Look at the parent root "VH" (וה) — connection of breath. The Waw + second Hay.

The Name is constructed from the language's most foundational existence-roots. This is not coincidence — it is design. When the Most High revealed His Name, He revealed the operating system of Hebrew itself.

Compare with Greco-Roman naming conventions. Zeus, Jupiter, Apollo — these are PROPER NOUNS, arbitrary labels for personalities. YHWH is a VERB-NAME — a functional schematic. The contrast tells you everything about which civilization understood ontology and which one was telling stories.

Use a whiteboard. Write Y-H-W-H vertically. Beside each letter, write the mechanical meaning. Then ask: "If we have lost the meaning of even these four letters, what else have we lost?"`,
      },
    ],
    keyPoints: [
      "Aahaya is a VERB, not a noun — the Creator IS the eternal act of being.",
      "YHWH is the functional blueprint: Inception → Breath → Connection → Manifestation.",
      "Every breath we take is a small echo of Aahaya.",
      "The Name is not a label, it is a working schematic of creation.",
    ],
  },
  {
    id: "lineage-misconceptions",
    number: 2,
    title: "Unit 2 — The Truth of the Lineage (Dispelling Misconceptions)",
    subtitle: 'Correcting the "Virgin" and "Immaculate Conception" narratives',
    foundation:
      "The Hebrew text never said what later translations claim. The damage to the lineage of Yeshua begins with one mistranslated word — and ends with a doctrine that pulled the entire faith out of its covenantal home.",
    sections: [
      {
        title: 'The Word "Almah" (עלמה) — What Isaiah Actually Said',
        content: `Isaiah 7:14 in Hebrew reads: "Hineh ha-ALMAH harah v'yoledet ben" — "Behold, the young woman is with child and bears a son."

The word is ALMAH (עלמה). It means:

• A young woman of marriageable age.
• A woman past puberty but typically before her first child.
• A woman in the prime of her bridal life.

It is a description of biological and social status. It is NOT a statement about sexual history.

Hebrew has a SEPARATE word for "virgin" in the sexual sense: BETULAH (בתולה). When the Torah wants to specify virginity, it uses Betulah (see Deuteronomy 22:14-19, where the question of physical virginity is the entire legal issue). Isaiah did NOT use Betulah. Isaiah used Almah.

Why does this matter? Because when the Hebrew Scriptures were translated into Greek (the Septuagint, around 250 BCE), the translators rendered Almah as PARTHENOS (παρθένος). Parthenos in older Greek could mean "young woman" too — but by the time the Greek New Testament was written, Parthenos had narrowed in popular usage to mean "physical virgin." The Greco-Roman world then read Matthew 1:23 (which quotes the Greek Septuagint) and concluded: "Aha — a miraculous virgin birth, just like our pagan god-myths."

But the Hebrew original never said that. It said: "A young woman of marriageable age will bear a son, and his name will be Immanu-El." A SIGN, yes — but the sign is the timing, the political moment, the covenantal name of the child, NOT a biological miracle outside the natural laws the Creator established in Genesis 1.`,
      },
      {
        title: "The Parents — Miriam and Yosef of the Tribe of Yahudah",
        content: `Yeshua was born to Miriam (Mary) and Yosef (Joseph), BOTH of the tribe of Yahudah, BOTH of the house of David (compare Matthew 1 and Luke 3 — different genealogies, same royal house).

Two consequences:

1. The Davidic claim is biological. Yeshua's right to sit on David's throne flows through Yosef as his legal father AND through Miriam as a direct Davidic descendant. If Yosef were not the biological father, the Davidic line would be broken (a man cannot inherit a throne through a woman in ancient Israelite law — this is why Numbers 27 had to ESTABLISH a special exception for the daughters of Zelophehad, and even then the inheritance came through her tribe, not the line). The "virgin birth" doctrine, taken literally, accidentally severs the very Davidic claim it tries to elevate.

2. Yeshua was a child of the covenant — not a divine outlier. He was circumcised on the 8th day (Luke 2:21). He was presented at the Temple (Luke 2:22). He kept Pesach with his family (Luke 2:41). He grew "in wisdom and stature" (Luke 2:52) — a Hebrew boy among Hebrew boys, learning Torah from his mother and his rabbis, learning his father's trade.

The Greco-Roman myth of a "virgin-born demigod" is COMMON in pagan literature (Perseus, Romulus, Mithras, Dionysus — all claimed virgin births by sky-fathers impregnating mortal women). The Hebrew narrative is the OPPOSITE: a normal birth to a covenant family, whose significance comes not from biological abnormality but from covenantal calling.

This unit's hardest pastoral work is gently helping students see that abandoning the "virgin birth" doctrine does NOT diminish Yeshua. It RESTORES him to his actual people, his actual lineage, his actual identity as Ben-Adam — Son of Man, son of the covenant, son of Yisrael.`,
      },
      {
        title: 'Teaching Strategy — Contrast "Virgin" and "Almah" Side by Side',
        content: `Concrete classroom exercise:

Draw two columns on the whiteboard.

Column 1 — English "Virgin" (post-Christian definition):
• Physical virginity required
• Miraculous birth outside biology
• Mother sets apart by sexual purity
• Echoes Greco-Roman demigod myths (Perseus, Mithras, Dionysus)
• Doctrine: Immaculate Conception (added 1854 CE by Pope Pius IX)

Column 2 — Hebrew Almah (Isaiah 7:14):
• A young woman of marriageable age
• Normal biological birth
• Mother set apart by COVENANT and TRIBE, not anatomy
• Echoes the covenant promises to David
• The SIGN is the timing and the child's NAME (Immanu-El = "El is with us"), not a biological anomaly

For each row, have the students physically TURN to face the column that matches the Hebrew text. By the end, the whole class is standing facing the Hebrew column. This is "lifting the veil" — they see, in their bodies, which side the Scripture actually supports.

End the lesson with the question: "If even ONE word can be quietly swapped (Almah → Parthenos → Virgin), how many other doctrines might be standing on similar mistranslations?" That question is the doorway into the rest of this module and the entire Forensic Etymology track.`,
      },
    ],
    keyPoints: [
      "Isaiah 7:14 says ALMAH (young woman), not BETULAH (virgin).",
      "Miriam and Yosef are BOTH Davidic — the throne-right is biological through both.",
      "Yeshua was a covenant child, circumcised, Torah-keeping, fully Hebrew.",
      'The "Immaculate Conception" doctrine was not formalized until 1854 CE.',
    ],
  },
  {
    id: "corporate-son",
    number: 3,
    title: "Unit 3 — The Corporate Son (The Nation of Yisrael)",
    subtitle: "Re-centering the narrative on Exodus 4:22",
    foundation:
      'When the Creator named His "firstborn son," He did not point at one man. He pointed at an entire nation.',
    sections: [
      {
        title: 'Exodus 4:22 — "Yisrael is My Firstborn Son"',
        content: `The clearest statement in the Hebrew Scriptures about who the "Son of the Most High" is comes in Exodus 4:22, when YHWH sends Mosheh to Pharaoh:

"And you shall say to Pharaoh: Thus says YHWH — Yisrael is My son, My firstborn."
(וְאָמַרְתָּ אֶל־פַּרְעֹה, כֹּה אָמַר יְהוָה, בְּנִי בְכֹרִי יִשְׂרָאֵל)

The Hebrew is unambiguous: BENI BECHORI YISRAEL — "My son, my firstborn, Yisrael." The "son" is the NATION. The firstborn-son language is CORPORATE, not individual.

This is reinforced everywhere in the Tanakh:

• Hosea 11:1 — "When Yisrael was a child, I loved him, and out of Egypt I called MY SON." (Matthew later applies this verse to Yeshua — but the original referent is the WHOLE NATION.)
• Jeremiah 31:9 — "I am a Father to Yisrael, and Ephraim is My firstborn."
• Deuteronomy 14:1 — "You are sons of YHWH your Elohim." (Plural — every Yisraelite.)
• Psalm 82:6 — "You are elohim; all of you are sons of the Most High."

The covenant model is CORPORATE SONSHIP. The whole nation is the firstborn. Every Yisraelite man, woman, and child is a "son of the Most High" by covenant. No individual person has ever been the SOLE son.`,
      },
      {
        title: "Yeshua — A Prophet From Within the Son, Not Above the Son",
        content: `Where does Yeshua fit in this corporate model?

He fits exactly where Mosheh, Avraham, David, Eliyahu, and the great prophets fit: as a LEADER and EXEMPLAR FROM WITHIN the nation that is the Son.

Hebrews 1:1-2 acknowledges this directly: "Long ago, at many times and in many ways, Elohim spoke to our fathers BY THE PROPHETS — but in these last days he has spoken to us by a SON." Yeshua is portrayed as the climactic prophet in a long line of Hebrew prophets, not as a creature of different ontology.

Compare:
• Mosheh — called "the most humble man on the face of the earth" (Numbers 12:3), spoke to the Creator face-to-face. A prophet from within the Son.
• Avraham — called "the friend of El" (Isaiah 41:8). A patriarch from within the Son (technically before the Son was named, but within the lineage).
• David — called "a man after the Creator's own heart" (1 Samuel 13:14). A king from within the Son.
• Yeshua — called "Rabbi" and "Master Teacher" by his disciples. A prophet, teacher, and Davidic claimant from within the Son.

Yeshua's role is to RESTORE the nation, RECALL them to Torah, and DEMONSTRATE in his own life what a fully covenant-faithful Yisraelite looks like. He is the model, not the replacement.

This is what Yeshua himself said: "I was not sent except to the lost sheep of the house of Yisrael" (Matthew 15:24). He came to call his people home. He is a son IN the Son, not a son INSTEAD of the Son.`,
      },
      {
        title: 'Teaching Strategy — "Family Inheritance"',
        content: `Use the metaphor of a family business or family inheritance to make this concrete.

Imagine a Hebrew family with a working olive press passed down for generations. The father has many sons. One son — let's call him the firstborn — carries the family NAME forward, oversees the press, and represents the family in legal matters. But every son in that family is still a son. The firstborn is not the ONLY son; he is the REPRESENTATIVE son.

Now scale it up. The Most High is the Father. Yisrael — the entire nation — is the firstborn son, carrying His Name and His Torah forward. Yeshua is a son IN the firstborn family, a representative leader, but every Yisraelite man and woman is also a son and daughter of the household.

Ask your students:
1. "If you are part of the firstborn family, what are you inheriting?"
2. "What does it mean for the WHOLE NATION to carry the Creator's Name?"
3. "If we forget that we are the Son, who do we hand the inheritance off to?"

This is identity work. Western Christian theology has trained students to think of themselves as OUTSIDERS being grafted in. The Hebrew text teaches them they are INSIDERS who were temporarily scattered. That shift — from outsider to insider — is the most important psychological and theological pivot in this entire module.`,
      },
    ],
    keyPoints: [
      "Exodus 4:22 names YISRAEL (the nation) as the firstborn son.",
      "Sonship in the Tanakh is CORPORATE — every covenant member is a son.",
      "Yeshua is a prophet from within the Son, not a replacement for the Son.",
      "The student's identity is INSIDER (firstborn family), not OUTSIDER (grafted-in convert).",
    ],
  },
  {
    id: "historical-veil-nicaea",
    number: 4,
    title: "Unit 4 — The Historical Veil (Council of Nicaea)",
    subtitle: "How the narrative was obscured in 325 CE",
    foundation:
      'The truth was not lost. It was COVERED. In 325 CE, a Roman emperor with no theological training imposed a settlement on a sea of Hebrew teachers — and the "veil" he laid over the Hebrew Oneness has been there ever since.',
    sections: [
      {
        title: "What Happened at Nicaea — The Political Backdrop",
        content: `Emperor Constantine I called the First Council of Nicaea in 325 CE. He was not a theologian. He was an emperor — and he had a problem.

His empire was fractured. He needed ONE unified religion to bind it back together. Christianity (such as it was at the time) was already widespread — but it was also wildly diverse. Different bishops in different cities taught different things about who Yeshua was, what the Creator was, what Torah meant, and whether the Hebrew Scriptures were even authoritative.

Constantine convened roughly 318 bishops at Nicaea (in modern-day İznik, Turkey). The dominant question on the table was the relationship between Yeshua and the Creator — a debate sparked by a presbyter named Arius (who taught that Yeshua was a created being, subordinate to the Father) versus a deacon named Athanasius (who taught that Yeshua was co-eternal and consubstantial with the Father).

Notice what was ALREADY missing from the table:
• No bishop representing the Hebrew "Yahudim" community (the Yerushalayim church had been scattered in 70 CE and again in 135 CE).
• No bishop arguing for Torah-observance as the defining test of covenant identity.
• No bishop arguing the corporate-sonship model from Exodus 4:22.

The debate was framed entirely within Greek philosophical categories — OUSIA (essence), HYPOSTASIS (subsistence), HOMOOUSIOS (same-essence). These are Plato's vocabulary, not Mosheh's.`,
      },
      {
        title: "The Shift — From Hebrew Oneness to Greek Trinity",
        content: `The settlement produced at Nicaea (refined later at Constantinople in 381 CE and Chalcedon in 451 CE) introduced the formal doctrine of the Trinity: one Creator existing as three co-eternal, co-equal "Persons" — Father, Son, and Holy Spirit.

This was a SHIFT, not a continuation. The original Hebrew confession is the Shema:

"SHEMA YISRAEL, YHWH ELOHEINU, YHWH ECHAD."
"Hear, O Yisrael: YHWH is our Elohim, YHWH is ONE." (Deuteronomy 6:4)

ECHAD (אחד) means ONE. It is the same word used in Genesis 2:24 for husband and wife becoming "one flesh" — a unity, yes, but a unity that does not require dividing the Creator's essence into three Persons. The Shema is the foundational identity statement of every Hebrew, recited twice daily for 3,000 years. It does NOT say "three in one." It says ONE.

Compare:
• Hebrew Oneness — YHWH is ONE; the Ruach (Spirit), the Davar (Word), and the visible Glory (Kavod) are MANIFESTATIONS of that One Creator, not separate "Persons."
• Greek Trinity — ONE God in THREE Persons, each fully God, each distinct, all co-eternal.

The Greek formula is a brilliant philosophical construct. It is also unrecognizable to any prophet of the Tanakh. Mosheh would not understand it. Yeshayahu (Isaiah) would not understand it. Yeshua himself — quoting the Shema in Mark 12:29 as the GREATEST commandment — appears to be teaching the Hebrew Oneness, not a Greek Trinity.`,
      },
      {
        title: "Constantine's Goal — A Romanized Faith",
        content: `Why did Constantine want this shift? Not because he had studied the Tanakh and found the Hebrew Oneness wanting. He had not studied the Tanakh.

He wanted a faith that was:

• Detachable from its Hebrew roots — because Hebrew identity was politically threatening to Rome (two Jewish revolts in 66-73 CE and 132-135 CE had cost the Empire enormous blood and money). A Hebrew-rooted faith looked like a future revolt waiting to happen.

• Palatable to a Greco-Roman audience — Greek philosophical categories felt familiar to educated Romans. Hebrew covenantal categories felt foreign.

• Centralized under his authority — One unified creed, one approved canon, one official priesthood (the bishops, now imperially funded), one set of holy days (gradually shifted from the Hebrew calendar to a solar Roman calendar — see the move from Pesach to "Easter" finalized at Nicaea itself).

In a single generation, the faith that began in a Yerushalayim upper room (Acts 1-2) was rebranded as a Roman imperial religion. The mother synagogue was edited out. The Torah was demoted. The Sabbath was moved from the seventh day to "the Lord's Day" (Sunday — the day of Sol Invictus, the Roman sun god whom Constantine had previously worshipped). The Hebrew prophets became "prefigurations" rather than authorities.

The veil was not laid maliciously by every bishop in the room. Many were sincere. But the FRAMEWORK had been changed — and once the framework is Greek, every subsequent translation, sermon, and creed will quietly reinforce the Greek framework, not the Hebrew one.`,
      },
      {
        title: 'Teaching Strategy — The "Veil" Analogy',
        content: `Bring a literal piece of heavy cloth into the classroom. A blanket works. A large tablecloth works.

Place an open Tanakh (or any Hebrew Scripture) on a table. Cover it with the cloth.

Say to the students: "For seventeen centuries, this cloth has been resting on the Hebrew text. The cloth is woven from Greek philosophy, Roman politics, and Latin liturgy. It is not evil cloth — it is just CLOTH. But it is not the Scripture underneath."

Now slowly lift the cloth.

"Our job in this curriculum, in Forensic Etymology, in the entire Yisrael Learning Center, is to LIFT THE VEIL. Not to destroy the cloth. Not to insult the people who wove it or the people who lived their lives under it. Just to lift it — gently — so we can read the original."

End by giving each student a small square of fabric (any color — gold for the Creator's glory if you can find it) and a small slip of paper with Deuteronomy 6:4 (the Shema) written on it. Have them place the paper on the table and lay the cloth on top. Then have them lift the cloth and read the Shema aloud together.

This is the closing image of the module. They will remember this moment. The Veil is real, but it is liftable. They have just lifted it.`,
      },
    ],
    keyPoints: [
      "Nicaea (325 CE) was a Roman political settlement, not a Hebrew theological discovery.",
      "ECHAD (Oneness) is the Hebrew confession; TRINITY is a Greek philosophical construct.",
      "Constantine wanted a Romanized faith detached from politically dangerous Hebrew roots.",
      "The veil is real, but it is liftable — and lifting it is the work of this curriculum.",
    ],
  },
];

function NameAndLineageModule({ onBack, user, gradeLevel = "Adult" }) {
  const [activeUnitIdx, setActiveUnitIdx] = useState(0);
  const [restartKey, setRestartKey] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({}); // { [unitId-sectionIdx]: text }
  const [expandingKey, setExpandingKey] = useState(null);
  // 2026-02-16: tappable scripture references. Clicking a verse pill
  // sets `openScriptureRef` to the citation string; the side panel
  // opens, hits the AI lookup, and renders Hebrew + English + context
  // + commentary. Per-citation cache lives inside ScriptureSidePanel.
  const [openScriptureRef, setOpenScriptureRef] = useState(null);
  const bodyRef = useRef(null);

  const activeUnit = UNITS[activeUnitIdx];

  // Concatenated text for InvisibleTeacher narration.
  const readingScript = useMemo(() => {
    if (!activeUnit) return "";
    return [
      `${activeUnit.title}. ${activeUnit.subtitle}`,
      `Foundation: ${activeUnit.foundation}`,
      ...activeUnit.sections.map((s, sIdx) => {
        const key = `${activeUnit.id}-${sIdx}`;
        return `${s.title}\n\n${expandedSections[key] || s.content}`;
      }),
      `Key Points: ${activeUnit.keyPoints.join("; ")}`,
    ]
      .filter(Boolean)
      .join("\n\n");
  }, [activeUnit, expandedSections]);

  const handleExpand = useCallback(async (sIdx, sectionText) => {
    const key = `${activeUnit.id}-${sIdx}`;
    setExpandingKey(key);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/classroom/voice-first/explain`,
        {
          topic: sectionText,
          grade_level: gradeLevel,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      const expanded =
        res.data?.explanation_text ||
        res.data?.explanation ||
        res.data?.expanded_text ||
        res.data?.content;
      if (expanded) {
        // Defensive: API contract is to return a string. If a future
        // change ever returns a structured object, coerce to JSON
        // string so we never crash React's render path with an
        // "Objects are not valid as a React child" (#31).
        const safe =
          typeof expanded === "string" ? expanded : JSON.stringify(expanded, null, 2);
        setExpandedSections((prev) => ({ ...prev, [key]: safe }));
      } else {
        alert("Deep-expand returned no content — please try again.");
      }
    } catch (e) {
      console.error("Name-and-Lineage deep-expand failed:", e);
      alert("Couldn't deep-expand right now — please try again shortly.");
    } finally {
      setExpandingKey(null);
    }
  }, [activeUnit, gradeLevel]);

  const handleRestart = useCallback(() => {
    clearLessonProgress(activeUnit?.title);
    setRestartKey((k) => k + 1);
    setExpandedSections({});
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeUnit]);

  const handleNextUnit = useCallback(() => {
    if (activeUnitIdx < UNITS.length - 1) {
      // Moving to a different unit — clear the previous unit's
      // saved teacher position so it doesn't bleed into the next
      // unit (which uses a different slug anyway).
      clearLessonProgress(activeUnit?.title);
      setActiveUnitIdx(activeUnitIdx + 1);
      setRestartKey((k) => k + 1);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [activeUnitIdx, activeUnit]);

  const handleDownloadPDF = useCallback(async () => {
    setPdfLoading(true);
    try {
      const { exportLessonPDF } = await import("../utils/pdfExport");
      await exportLessonPDF({
        title: activeUnit.title,
        subtitle: `The Name and the Lineage · ${activeUnit.subtitle}`,
        bodyNode: bodyRef.current,
        studentName: user?.full_name || user?.username,
        gradeLevel,
      });
    } catch (e) {
      console.error("PDF export failed:", e);
      alert("PDF export failed. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  }, [activeUnit, user, gradeLevel]);

  return (
    <div data-testid="name-and-lineage-module" style={{ maxWidth: "1000px", margin: "0 auto" }}>
      {/* Unit Selector */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          marginBottom: "20px",
          background: "#111",
          padding: "12px",
          borderRadius: "10px",
          border: "1px solid #4a2a5a",
        }}
      >
        {UNITS.map((u, idx) => (
          <button
            key={u.id}
            data-testid={`name-lineage-unit-${idx + 1}`}
            onClick={() => {
              setActiveUnitIdx(idx);
              setRestartKey((k) => k + 1);
            }}
            style={{
              flex: "1 1 auto",
              minWidth: "200px",
              padding: "10px 14px",
              borderRadius: "8px",
              background:
                idx === activeUnitIdx
                  ? "linear-gradient(135deg, #4a2a5a 0%, #6a3a7a 100%)"
                  : "#1a1a1a",
              color: idx === activeUnitIdx ? "#fff" : "#888",
              border: `1px solid ${idx === activeUnitIdx ? "#DDA0DD" : "#333"}`,
              fontWeight: idx === activeUnitIdx ? "bold" : "normal",
              cursor: "pointer",
              fontSize: "0.85rem",
              textAlign: "left",
            }}
          >
            Unit {u.number}: {u.title.split("—")[1]?.trim() || u.title}
          </button>
        ))}
      </div>

      <div ref={bodyRef} key={restartKey}>
        {/* Header */}
        <div
          className="card"
          style={{
            background: "linear-gradient(135deg, #2a1a3a 0%, #1a0a2a 100%)",
            borderTop: "4px solid #DDA0DD",
          }}
        >
          <h2 style={{ color: "#DDA0DD", margin: 0 }}>{activeUnit.title}</h2>
          <p style={{ color: "#FFA500", marginTop: 6, marginBottom: 12 }}>
            {activeUnit.subtitle}
          </p>
          <div
            style={{
              color: "#ccc",
              fontSize: "0.95rem",
              fontStyle: "italic",
              padding: "12px",
              background: "rgba(221,160,221,0.07)",
              borderLeft: "3px solid #DDA0DD",
              borderRadius: "4px",
            }}
          >
            <strong style={{ color: "#DDA0DD" }}>Foundation:</strong>{" "}
            {linkifyScripture(activeUnit.foundation, setOpenScriptureRef)}
          </div>
        </div>

        {/* 2026-02-21 YLC Motion Studio — cartoon helper per unit. */}
        <AnimatedHelper
          key={`nal-helper-${activeUnit.id}`}
          lessonId={`nal-unit-${activeUnitIdx + 1}`}
          prompt={`Name and Lineage: ${activeUnit.title}`}
          gradeTier="Seminary"
          narrationText={`${activeUnit.title}. ${activeUnit.subtitle || ""} ${activeUnit.foundation || ""}`.slice(0, 900)}
          lessonTitle={activeUnit.title}
          variant="banner"
        />

        {/* Sections */}
        {activeUnit.sections.map((s, sIdx) => {
          const key = `${activeUnit.id}-${sIdx}`;
          const isExpanded = !!expandedSections[key];
          const isExpanding = expandingKey === key;
          const renderedBody = expandedSections[key] || s.content;
          return (
            <div
              key={sIdx}
              className="card"
              data-testid={`name-lineage-section-${sIdx}`}
              style={{ background: "#1a1a1a", borderLeft: "4px solid #DDA0DD" }}
            >
              <h4 style={{ color: "#DDA0DD", marginBottom: "12px" }}>{s.title}</h4>
              <div
                style={{
                  color: "#ddd",
                  lineHeight: "1.8",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.95rem",
                }}
              >
                {linkifyScripture(renderedBody, setOpenScriptureRef)}
              </div>
              <div style={{ marginTop: "12px" }}>
                <button
                  onClick={() => handleExpand(sIdx, s.content)}
                  disabled={isExpanding || isExpanded}
                  data-testid={`name-lineage-expand-${sIdx}`}
                  style={{
                    background: isExpanded
                      ? "#1a2a1a"
                      : "linear-gradient(135deg, #2a1a3a 0%, #4a2a5a 100%)",
                    color: isExpanded ? "#90EE90" : "#DDA0DD",
                    border: "1px solid #DDA0DD",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "0.85rem",
                    cursor: isExpanding ? "wait" : isExpanded ? "default" : "pointer",
                  }}
                >
                  {isExpanding
                    ? "Expanding…"
                    : isExpanded
                    ? "✓ Expanded with AI"
                    : "📖 Deep-Expand with AI"}
                </button>
              </div>
            </div>
          );
        })}

        {/* Key Points */}
        <div
          className="card"
          style={{
            background: "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)",
            borderLeft: "4px solid #90EE90",
          }}
        >
          <h4 style={{ color: "#90EE90", marginBottom: "12px" }}>📌 Key Points</h4>
          <ul style={{ color: "#ddd", lineHeight: "1.8", paddingLeft: "20px" }}>
            {activeUnit.keyPoints.map((kp, i) => (
              <li key={i} style={{ marginBottom: "8px" }}>
                {linkifyScripture(kp, setOpenScriptureRef)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* THE INVISIBLE TEACHER */}
      <InvisibleTeacher
        key={`nl-it-${activeUnit.id}-${restartKey}`}
        gradeLevel={gradeLevel}
        lessonContent={readingScript}
        lessonTitle={activeUnit.title}
        user={user}
        onSectionStart={(idx) => {
          try {
            window.dispatchEvent(
              new CustomEvent("ylc:teacher-section-start", { detail: { idx } })
            );
          } catch {
            /* non-fatal */
          }
        }}
      />

      {/* Standardized 5-button action bar */}
      <StandardLessonActions
        onBack={onBack}
        onRestart={handleRestart}
        onTakeQuiz={() => {
          alert(
            "Unit quizzes for The Name and the Lineage are launching soon. For now, discuss the four Key Points with your study partner."
          );
        }}
        onNextLesson={activeUnitIdx < UNITS.length - 1 ? handleNextUnit : undefined}
        nextLessonLabel={UNITS[activeUnitIdx + 1]?.title}
        onDownloadPDF={handleDownloadPDF}
        pdfLoading={pdfLoading}
      />

      {/* Slide-in scripture side panel. Opens when a verse pill is
          tapped — shows Hebrew + English + context + commentary. */}
      <ScriptureSidePanel
        reference={openScriptureRef}
        onClose={() => setOpenScriptureRef(null)}
        gradeLevel={gradeLevel}
      />
    </div>
  );
}

export default NameAndLineageModule;
